import { Slot } from '@radix-ui/react-slot';
import { MoreHorizontal } from 'lucide-react';

import type { ComponentProps, JSX } from 'react';

import { cn } from '~/utils';
import { CaretRightIcon } from '~/components/icons';

export function Breadcrumb(props: ComponentProps<'nav'>): JSX.Element {
  return <nav aria-label='breadcrumb' data-slot='breadcrumb' {...props} />;
}

export function BreadcrumbList({
  className,
  ...props
}: ComponentProps<'ol'>): JSX.Element {
  return (
    <ol
      data-slot='breadcrumb-list'
      className={cn(
        'text-muted-foreground flex flex-wrap items-center text-sm break-words',
        'gap-4',
        className,
      )}
      {...props}
    />
  );
}

export function BreadcrumbItem({
  className,
  ...props
}: ComponentProps<'li'>): JSX.Element {
  return (
    <li
      data-slot='breadcrumb-item'
      className={cn('inline-flex items-center gap-1.5', className)}
      {...props}
    />
  );
}

interface IBreadcrumbLinkProps extends ComponentProps<'a'> {
  asChild?: boolean;
}

export function BreadcrumbLink({
  asChild,
  className,
  ...props
}: IBreadcrumbLinkProps): JSX.Element {
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      data-slot='breadcrumb-link'
      className={cn(
        'hover:text-foreground transition-colors',
        'text-md leading-[130%] font-medium',
        className,
      )}
      {...props}
    />
  );
}

export function BreadcrumbPage({
  className,
  ...props
}: ComponentProps<'span'>): JSX.Element {
  return (
    <span
      role='link'
      aria-current='page'
      aria-disabled='true'
      data-slot='breadcrumb-page'
      className={cn('font-medium text-md leading-[130%]', className)}
      {...props}
    />
  );
}

export function BreadcrumbSeparator({
  children,
  className,
  ...props
}: ComponentProps<'li'>): JSX.Element {
  return (
    <li
      aria-hidden='true'
      role='presentation'
      data-slot='breadcrumb-separator'
      className={cn('[&>svg]:size-4', className)}
      {...props}
    >
      {children ?? <CaretRightIcon />}
    </li>
  );
}

export function BreadcrumbEllipsis({
  className,
  ...props
}: ComponentProps<'span'>): JSX.Element {
  return (
    <span
      aria-hidden='true'
      role='presentation'
      data-slot='breadcrumb-ellipsis'
      className={cn('flex size-9 items-center justify-center', className)}
      {...props}
    >
      <MoreHorizontal className='size-4' />
      <span className='sr-only'>More</span>
    </span>
  );
}
